import { useMemo, useState } from 'react';
import { Icon } from '../utils/icons';

const DonutChart = ({ data, title, className = "" }) => {
    const [hoveredSegment, setHoveredSegment] = useState(null);
    const [tooltipData, setTooltipData] = useState(null);
    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

    const chartData = useMemo(() => {
        if (!data || data.length === 0) {
            return [];
        }

        const total = data.reduce((sum, item) => sum + (item.count || 0), 0);
        // Soft, beautiful color palette
        const colors = [
            '#8B5CF6', // Soft purple
            '#06B6D4', // Soft cyan  
            '#10B981', // Soft emerald
            '#F59E0B', // Soft amber
            '#EF4444', // Soft red
            '#8B5A2B', // Soft brown
            '#6B7280', // Soft gray
        ];

        let cumulativePercentage = 0;

        const result = data.map((item, index) => {
            const percentage = total > 0 ? (item.count / total) * 100 : 0;
            const startAngle = cumulativePercentage * 3.6; // Convert percentage to degrees
            const endAngle = (cumulativePercentage + percentage) * 3.6;
            cumulativePercentage += percentage;

            return {
                label: item._id || item.label || 'Unknown',
                value: item.count || 0,
                percentage: Math.round(percentage * 10) / 10,
                color: colors[index % colors.length],
                startAngle,
                endAngle,
            };
        });

        return result;
    }, [data]);

    const createPath = (startAngle, endAngle, innerRadius = 70, outerRadius = 110) => {
        const start = polarToCartesian(110, 110, outerRadius, endAngle);
        const end = polarToCartesian(110, 110, outerRadius, startAngle);
        const innerStart = polarToCartesian(110, 110, innerRadius, endAngle);
        const innerEnd = polarToCartesian(110, 110, innerRadius, startAngle);

        const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

        return [
            "M", start.x, start.y,
            "A", outerRadius, outerRadius, 0, largeArcFlag, 0, end.x, end.y,
            "L", innerEnd.x, innerEnd.y,
            "A", innerRadius, innerRadius, 0, largeArcFlag, 1, innerStart.x, innerStart.y,
            "Z"
        ].join(" ");
    };

    const polarToCartesian = (centerX, centerY, radius, angleInDegrees) => {
        const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
        return {
            x: centerX + (radius * Math.cos(angleInRadians)),
            y: centerY + (radius * Math.sin(angleInRadians))
        };
    };

    const getDeviceIcon = (deviceType) => {
        const deviceName = deviceType?.toLowerCase();
        let iconName;

        switch (deviceName) {
            case 'mobile':
                iconName = 'mobile';
                break;
            case 'tablet':
                iconName = 'tablet';
                break;
            case 'desktop':
            default:
                iconName = 'desktop';
                break;
        }

        return <Icon name={iconName} size={16} className="text-gray-600" />;
    };

    const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);

    if (!chartData.length || totalValue === 0) {
        return (
            <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
                <h3 className="text-lg font-semibold text-gray-900 mb-6">{title}</h3>
                <div className="flex flex-col items-center justify-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <Icon name="computer" size={32} className="text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-600 mb-1">No device data available</p>
                    <p className="text-xs text-gray-400 text-center">Device breakdown will appear once widgets receive traffic</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className} flex flex-col h-full`}>
            <h3 className="text-lg font-semibold text-gray-900 mb-6">{title}</h3>

            <div className="flex-1 flex flex-col justify-between">
                {/* Centered Donut Chart */}
                <div className="flex justify-center items-center flex-1 min-h-64">
                    <div className="relative">
                        <svg
                            width="220"
                            height="220"
                            className="transform -rotate-90"
                            onMouseMove={(e) => {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setMousePosition({
                                    x: e.clientX - rect.left,
                                    y: e.clientY - rect.top
                                });
                            }}
                            onMouseLeave={() => {
                                setHoveredSegment(null);
                                setTooltipData(null);
                            }}
                        >
                            {chartData.map((segment, index) => {
                                const isHovered = hoveredSegment === index;
                                const scale = isHovered ? 1.05 : 1;
                                const opacity = hoveredSegment !== null && !isHovered ? 0.6 : 1;

                                return (
                                    <g key={index}>
                                        <path
                                            d={createPath(segment.startAngle, segment.endAngle, 70, 110)}
                                            fill={segment.color}
                                            stroke="white"
                                            strokeWidth={isHovered ? "3" : "2"}
                                            style={{
                                                opacity,
                                                transform: `scale(${scale})`,
                                                transformOrigin: '110px 110px',
                                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                                filter: isHovered ? 'drop-shadow(0 4px 12px rgba(0,0,0,0.15))' : 'none'
                                            }}
                                            className="cursor-pointer"
                                            onMouseEnter={() => {
                                                setHoveredSegment(index);
                                                setTooltipData({
                                                    label: segment.label,
                                                    value: segment.value,
                                                    percentage: segment.percentage,
                                                    color: segment.color
                                                });
                                            }}
                                        />
                                    </g>
                                );
                            })}

                            {/* Inner circle with total */}
                            <circle
                                cx="110"
                                cy="110"
                                r="70"
                                fill="white"
                                stroke="#f1f5f9"
                                strokeWidth="1"
                            />
                        </svg>

                        {/* Center content */}
                        <div className="absolute inset-0 flex flex-col items-center justify-center">
                            {hoveredSegment !== null ? (
                                <div className="text-center transition-all duration-300">
                                    <div className="text-2xl font-bold text-gray-900">
                                        {chartData[hoveredSegment].value.toLocaleString()}
                                    </div>
                                    <div className="text-xs text-gray-500 font-medium mt-1 capitalize">
                                        {chartData[hoveredSegment].label}
                                    </div>
                                    <div className="text-lg font-semibold mt-1" style={{ color: chartData[hoveredSegment].color }}>
                                        {chartData[hoveredSegment].percentage}%
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center transition-all duration-300">
                                    <div className="text-3xl font-bold text-gray-900">{totalValue.toLocaleString()}</div>
                                    <div className="text-sm text-gray-500 font-medium mt-1">Total Views</div>
                                </div>
                            )}
                        </div>

                        {/* Tooltip */}
                        {tooltipData && (
                            <div
                                className="absolute z-30 bg-gray-900 text-white px-3 py-2 rounded-lg shadow-xl pointer-events-none border border-gray-700"
                                style={{
                                    left: Math.max(50, Math.min(mousePosition.x, 170)), // Keep within bounds
                                    top: Math.max(10, mousePosition.y - 60), // Position above cursor
                                    opacity: tooltipData ? 1 : 0,
                                    transition: 'opacity 0.15s ease-out',
                                    transform: `translate(-50%, 0) scale(${tooltipData ? 1 : 0.9})`,
                                    transformOrigin: 'bottom center'
                                }}
                            >
                                <div className="flex items-center space-x-2 mb-1">
                                    <div
                                        className="w-3 h-3 rounded-full"
                                        style={{ backgroundColor: tooltipData.color }}
                                    />
                                    <span className="text-sm font-semibold capitalize">{tooltipData.label}</span>
                                </div>
                                <div className="text-xs text-gray-300 space-y-0.5">
                                    <div className="font-medium">{tooltipData.value.toLocaleString()} views</div>
                                    <div>{tooltipData.percentage}% of total</div>
                                </div>
                                {/* Tooltip arrow */}
                                <div
                                    className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0"
                                    style={{
                                        borderLeft: '5px solid transparent',
                                        borderRight: '5px solid transparent',
                                        borderTop: '5px solid #1f2937'
                                    }}
                                />
                            </div>
                        )}
                    </div>
                </div>

                {/* Bottom-aligned Legend and Stats */}
                <div className="mt-6">
                    {/* Device Legend */}
                    <div className="flex flex-wrap justify-center gap-3 mb-6">
                        {chartData.map((segment, index) => {
                            const isHovered = hoveredSegment === index;
                            return (
                                <div
                                    key={index}
                                    className={`flex items-center px-4 py-3 rounded-lg border transition-opacity duration-300 ${isHovered
                                        ? 'bg-white border-gray-300 shadow-lg opacity-100'
                                        : hoveredSegment !== null
                                            ? 'bg-gray-50 border-gray-200 shadow-sm opacity-50'
                                            : 'bg-gray-50 border-gray-200 shadow-sm opacity-100'
                                        }`}
                                >
                                    <div
                                        className="w-3 h-3 rounded-full flex-shrink-0 ring-2 ring-white shadow-sm mr-3"
                                        style={{ backgroundColor: segment.color }}
                                    />
                                    <div className="flex items-center space-x-2">
                                        <div className="text-gray-600">
                                            {getDeviceIcon(segment.label)}
                                        </div>
                                        <span className="text-sm font-medium text-gray-800 capitalize">
                                            {segment.label}
                                        </span>
                                    </div>
                                    <div className="ml-3 text-sm font-semibold text-gray-900 bg-white px-2 py-1 rounded-md border border-gray-200">
                                        {segment.percentage}%
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Summary stats */}
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                        <div className="grid grid-cols-3 gap-4 text-center">
                            <div>
                                <div className="text-lg font-bold text-gray-900">{chartData.length}</div>
                                <div className="text-xs text-gray-500 font-medium">Device Types</div>
                            </div>
                            <div>
                                <div className="text-lg font-bold text-gray-900">
                                    {chartData.find(d => d.percentage === Math.max(...chartData.map(s => s.percentage)))?.percentage}%
                                </div>
                                <div className="text-xs text-gray-500 font-medium">Top Device</div>
                            </div>
                            <div>
                                <div className="text-lg font-bold text-gray-900">{totalValue.toLocaleString()}</div>
                                <div className="text-xs text-gray-500 font-medium">Total Views</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DonutChart;
